// @tennis-matches/services/trainerstunden/billing-statement-invoice-freeze
// Unveränderlicher Rechnungsinhalt ab Versand (Snapshots + Freeze).

import { formatPostalAddressLine } from "../masterdata/index.js";
import { isTrainerstundenBillingMissingColumnError } from "./billing-statement-db-compat.js";
import {
  buildInvoiceLayoutSnapshot,
  loadInvoiceLayoutVersions,
  resolveInvoiceLayoutVersionForDate,
} from "./invoice-layout-versions.js";
import {
  computeInvoiceVatTotals,
  resolveTrainerInvoiceVat,
} from "./invoice-vat.js";
import {
  formatBillingStatementItemTitle,
  resolveBillingStatementItemSession,
} from "./billing-statement-item-display.js";
import {
  collectBillingStatementIssuerTrainerLookupIds,
  decodeBillingStatementIssuerChoice,
  resolveBillingStatementIssuerTrainerId,
  resolveBillingStatementIssuerPreferenceForPreview,
  resolvePlannerTrainerIdFromList,
} from "./billing-statement-issuer.js";
import { loadBillingStatementRecipientSsotRow } from "./billing-statement-recipient-email.js";

const SESSION_LINE_SNAPSHOT_SELECT =
  "id,trainer_id,date,start_time,duration_min,training_type";

/** @type {(key: string, params?: Record<string, unknown>) => string} */
function __billingItemDisplayT(key, params = {}) {
  const p = params && typeof params === "object" ? params : {};
  if (key === "trainerstunden.billingStatements.item.sessionLine") {
    return `${__asStr(p.training)} von ${__asStr(p.timeRange)} Uhr`;
  }
  if (key === "trainerstunden.training.single") return "Einzeltraining";
  if (key === "trainerstunden.training.pair2") return "Doppeltraining";
  if (key === "trainerstunden.training.group") return "Gruppentraining";
  if (key === "trainerstunden.planning.fieldDash") return "—";
  if (key === "trainerstunden.billingInbox.itemTitleFallback")
    return "Leistung";
  return key;
}

const FROZEN_STATUSES = new Set(["sent", "partially_paid", "paid"]);

/** PostgREST-Spalten für Empfänger-Stammdaten (ohne legacy `address`; SSOT: strukturierte Felder). */
export const RECIPIENT_POSTAL_SELECT =
  "id,first_name,last_name,email,phone,street,house_number,postal_code,city,country";

function __asStr(x) {
  return String(x ?? "").trim();
}

/**
 * @param {object | null | undefined} statement
 */
export function isBillingStatementInvoiceFrozen(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  return FROZEN_STATUSES.has(__asStr(st.status));
}

/**
 * Entwurf ohne festen Rechnungsinhalt (live Vorschau / Korrektur).
 *
 * @param {object | null | undefined} statement
 */
export function isBillingStatementLiveDraft(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const status = (__asStr(st.status) || "draft").toLowerCase();
  return status === "draft";
}

/**
 * @param {object | null | undefined} row
 */
export function buildPostalAddressSnapshotFromRow(row) {
  const r = row && typeof row === "object" ? row : null;
  if (!r) return "";
  return formatPostalAddressLine(r) || __asStr(r.address);
}

/**
 * @param {{ recipient_type?: string, person?: object | null, contact?: object | null }} input
 */
export function buildBillingRecipientSnapshotFields({
  recipient_type,
  person,
  contact,
}) {
  const rt = __asStr(recipient_type);
  if (rt === "contact") {
    const c = contact && typeof contact === "object" ? contact : {};
    const name = `${__asStr(c.first_name)} ${__asStr(c.last_name)}`.trim();
    return {
      recipient_name_snapshot: name,
      recipient_email_snapshot: __asStr(c.email),
      recipient_phone_snapshot: __asStr(c.phone),
      recipient_address_snapshot: buildPostalAddressSnapshotFromRow(c),
    };
  }
  const p = person && typeof person === "object" ? person : {};
  const name = `${__asStr(p.first_name)} ${__asStr(p.last_name)}`.trim();
  return {
    recipient_name_snapshot: name,
    recipient_email_snapshot: __asStr(p.email),
    recipient_phone_snapshot: __asStr(p.phone),
    recipient_address_snapshot: buildPostalAddressSnapshotFromRow(p),
  };
}

/**
 * @param {object | null | undefined} trainer
 */
export function buildBillingIssuerSnapshotFields(trainer) {
  const tr = trainer && typeof trainer === "object" ? trainer : null;
  if (!tr) {
    return {
      issuer_trainer_id_snapshot: "",
      issuer_name_snapshot: "",
      issuer_email_snapshot: "",
      issuer_address_snapshot: "",
    };
  }
  const name = `${__asStr(tr.first_name)} ${__asStr(tr.last_name)}`.trim();
  return {
    issuer_trainer_id_snapshot: __asStr(tr.id),
    issuer_name_snapshot: name,
    issuer_email_snapshot: __asStr(tr.login_email),
    issuer_address_snapshot:
      buildPostalAddressSnapshotFromRow(tr) || __asStr(tr.address),
  };
}

import {
  pickActiveTrainerPaymentMethods,
  snapshotActiveTrainerPaymentMethods,
  snapshotTrainerPaymentMethod,
} from "./invoice-payment-snapshot.js";

/**
 * @param {object[]} methods
 */
export function pickDefaultTrainerPaymentMethodSnapshot(methods) {
  const picked = pickActiveTrainerPaymentMethods(methods)[0] || null;
  return snapshotTrainerPaymentMethod(picked);
}

/**
 * @param {object[]} items
 * @param {object[]} sessions
 */
export function resolveIssuerTrainerIdFromBillingItems(items, sessions) {
  const sessionIds = Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((it) => __asStr(it?.session_id))
        .filter(Boolean),
    ),
  );
  if (!sessionIds.length) return "";
  const sessionById = new Map(
    (Array.isArray(sessions) ? sessions : []).map((s) => [__asStr(s?.id), s]),
  );
  const counts = new Map();
  for (const sid of sessionIds) {
    const tid = __asStr(sessionById.get(sid)?.trainer_id);
    if (!tid) continue;
    counts.set(tid, (counts.get(tid) || 0) + 1);
  }
  let bestId = "";
  let best = 0;
  for (const [tid, c] of counts) {
    if (c > best) {
      best = c;
      bestId = tid;
    }
  }
  return bestId;
}

function __personFirstNameSnapshot(person) {
  const p = person && typeof person === "object" ? person : {};
  return __asStr(p.first_name) || __asStr(p.last_name);
}

/** @param {object | null | undefined} person */
export function buildBillingPersonFirstNameSnapshot(person) {
  return __personFirstNameSnapshot(person);
}

/**
 * Entwurf mit gespeichertem Empfänger-Snapshot (historisch). Kontakt-Anzeige/Versand liest
 * für Entwürfe wieder live aus den Stammdaten; Snapshots gelten erst ab Versand-Freeze.
 * @param {object | null | undefined} statement
 */
export function isBillingStatementDraftMasterDataFrozen(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (__asStr(st.status) !== "draft") return false;
  return Boolean(__asStr(st.recipient_name_snapshot));
}

/**
 * Beim Anlegen eines Rechnungsentwurfs Empfänger- und Spielernamen persistieren (vor Versand-Freeze).
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} statementId
 */
export async function snapshotBillingStatementDraftMasterData(
  supabase,
  statementId,
) {
  const sb = supabase;
  const sid = __asStr(statementId);
  if (!sb || !sid) return { ok: false, reason: "missing-input" };

  const { data: st, error: stErr } = await sb
    .from("trainerstunden_billing_statements")
    .select("*")
    .eq("id", sid)
    .maybeSingle();
  if (stErr) return { ok: false, reason: "db-error", error: stErr };
  if (!st) return { ok: false, reason: "not-found" };
  if (__asStr(st.status) !== "draft") return { ok: true, skipped: true };

  const { data: items, error: itemsErr } = await sb
    .from("trainerstunden_billing_statement_items")
    .select("id,person_id,person_name_snapshot")
    .eq("statement_id", sid)
    .limit(2000);
  if (itemsErr) return { ok: false, reason: "db-error-items", error: itemsErr };

  const itemList = Array.isArray(items) ? items : [];
  const rt = __asStr(st.recipient_type);

  const recipientRow = await loadBillingStatementRecipientSsotRow(sb, st);

  const recipientSnaps = buildBillingRecipientSnapshotFields({
    recipient_type: rt,
    person: rt === "contact" ? null : recipientRow,
    contact: rt === "contact" ? recipientRow : null,
  });

  const headerPatch = {};
  for (const [key, value] of Object.entries(recipientSnaps)) {
    if (!__asStr(st[key]) && __asStr(value)) headerPatch[key] = value;
  }

  if (Object.keys(headerPatch).length) {
    const { error: hErr } = await sb
      .from("trainerstunden_billing_statements")
      .update(headerPatch)
      .eq("id", sid);
    if (hErr && !isTrainerstundenBillingMissingColumnError(hErr)) {
      return { ok: false, reason: "db-error-header", error: hErr };
    }
  }

  const peopleIds = Array.from(
    new Set(itemList.map((it) => __asStr(it?.person_id)).filter(Boolean)),
  );
  const peopleById = new Map();
  if (peopleIds.length) {
    const pplR = await sb
      .from("trainerstunden_people")
      .select("id,first_name,last_name")
      .in("id", peopleIds.slice(0, 500));
    if (!pplR.error) {
      for (const p of pplR.data || []) peopleById.set(__asStr(p.id), p);
    }
  }

  for (const it of itemList) {
    if (__asStr(it?.person_name_snapshot)) continue;
    const pid = __asStr(it?.person_id);
    const person = pid ? peopleById.get(pid) : null;
    const personName = __personFirstNameSnapshot(person);
    if (!personName) continue;
    const { error: lineErr } = await sb
      .from("trainerstunden_billing_statement_items")
      .update({ person_name_snapshot: personName })
      .eq("id", __asStr(it.id));
    if (lineErr && !isTrainerstundenBillingMissingColumnError(lineErr)) {
      return { ok: false, reason: "db-error-item-snapshot", error: lineErr };
    }
  }

  return { ok: true };
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} statementId
 */
export async function freezeBillingStatementInvoiceDocument(
  supabase,
  statementId,
) {
  const sb = supabase;
  const sid = __asStr(statementId);
  if (!sb || !sid) return { ok: false, reason: "missing-input" };

  const { data: st, error: stErr } = await sb
    .from("trainerstunden_billing_statements")
    .select("*")
    .eq("id", sid)
    .maybeSingle();
  if (stErr) return { ok: false, reason: "db-error", error: stErr };
  if (!st) return { ok: false, reason: "not-found" };

  if (isBillingStatementInvoiceFrozen(st)) {
    return { ok: true, data: st, alreadyFrozen: true };
  }

  const status = __asStr(st.status);
  if (status === "cancelled") return { ok: false, reason: "cancelled" };

  const { data: items, error: itemsErr } = await sb
    .from("trainerstunden_billing_statement_items")
    .select("*")
    .eq("statement_id", sid)
    .order("date", { ascending: true })
    .limit(2000);
  if (itemsErr) return { ok: false, reason: "db-error-items", error: itemsErr };

  const itemList = Array.isArray(items) ? items : [];
  const rt = __asStr(st.recipient_type);
  const recipientRow = await loadBillingStatementRecipientSsotRow(sb, st);

  const recipientSnaps = buildBillingRecipientSnapshotFields({
    recipient_type: rt,
    person: rt === "contact" ? null : recipientRow,
    contact: rt === "contact" ? recipientRow : null,
  });

  const sessionIds = Array.from(
    new Set(itemList.map((it) => __asStr(it?.session_id)).filter(Boolean)),
  );
  let sessions = [];
  if (sessionIds.length) {
    const sessR = await sb
      .from("trainerstunden_sessions")
      .select(SESSION_LINE_SNAPSHOT_SELECT)
      .in("id", sessionIds.slice(0, 500));
    if (!sessR.error) sessions = sessR.data || [];
  }
  const sessionsById = new Map(sessions.map((s) => [__asStr(s?.id), s]));

  const issuerPreference =
    resolveBillingStatementIssuerPreferenceForPreview(st);
  const explicitDraftIssuerId = decodeBillingStatementIssuerChoice(
    __asStr(st.draft_issuer_trainer_id),
  ).trainerId;
  let trainersForIssuer = [];
  const sessionTrainerIds = Array.from(
    new Set(
      itemList
        .map((it) =>
          __asStr(sessionsById.get(__asStr(it?.session_id))?.trainer_id),
        )
        .filter(Boolean),
    ),
  );
  if (sessionTrainerIds.length) {
    const trR = await sb
      .from("trainerstunden_trainers")
      .select(
        "id,first_name,last_name,login_email,login_role,coordination_periods,address,street,house_number,postal_code,city,country,invoice_vat_mode,invoice_vat_rate,tenant_id",
      )
      .in("id", sessionTrainerIds.slice(0, 100));
    if (!trR.error) trainersForIssuer = trR.data || [];
  }
  const issuerLookupIds = collectBillingStatementIssuerTrainerLookupIds({
    items: itemList,
    sessions,
    trainers: trainersForIssuer,
  }).filter((id) => !trainersForIssuer.some((tr) => __asStr(tr?.id) === id));
  if (issuerLookupIds.length) {
    const extraR = await sb
      .from("trainerstunden_trainers")
      .select(
        "id,first_name,last_name,login_email,login_role,coordination_periods,address,street,house_number,postal_code,city,country,invoice_vat_mode,invoice_vat_rate,tenant_id",
      )
      .in("id", issuerLookupIds.slice(0, 100));
    if (!extraR.error && extraR.data?.length) {
      trainersForIssuer = [...trainersForIssuer, ...extraR.data];
    }
  }
  if (
    explicitDraftIssuerId &&
    !trainersForIssuer.some((tr) => __asStr(tr?.id) === explicitDraftIssuerId)
  ) {
    const draftTrR = await sb
      .from("trainerstunden_trainers")
      .select(
        "id,first_name,last_name,login_email,login_role,coordination_periods,address,street,house_number,postal_code,city,country,invoice_vat_mode,invoice_vat_rate,tenant_id",
      )
      .eq("id", explicitDraftIssuerId)
      .maybeSingle();
    if (!draftTrR.error && draftTrR.data) {
      trainersForIssuer = [...trainersForIssuer, draftTrR.data];
    }
  }
  let issuerTrainerId =
    resolveBillingStatementIssuerTrainerId({
      items: itemList,
      sessions,
      trainers: trainersForIssuer,
      preferredTrainerId: issuerPreference || explicitDraftIssuerId,
      coordinatorTrainerId: resolvePlannerTrainerIdFromList(trainersForIssuer),
    }) || resolveIssuerTrainerIdFromBillingItems(itemList, sessions);
  // C1: Persistierter Draft-Aussteller ist verbindlich — keine Live-Neubewertung.
  if (explicitDraftIssuerId && status === "draft") {
    issuerTrainerId = explicitDraftIssuerId;
  }
  if (!issuerTrainerId) {
    return { ok: false, reason: "missing-issuer" };
  }

  let issuerTrainer = null;
  if (issuerTrainerId) {
    const trR = await sb
      .from("trainerstunden_trainers")
      .select(
        "id,tenant_id,first_name,last_name,login_email,address,street,house_number,postal_code,city,country,invoice_vat_mode,invoice_vat_rate",
      )
      .eq("id", issuerTrainerId)
      .maybeSingle();
    if (!trR.error) issuerTrainer = trR.data;
  }

  if (explicitDraftIssuerId && !issuerTrainer) {
    return { ok: false, reason: "issuer-not-found" };
  }
  const statementTenantId = __asStr(st?.tenant_id);
  const issuerTenantId = __asStr(issuerTrainer?.tenant_id);
  if (
    statementTenantId &&
    issuerTenantId &&
    statementTenantId !== issuerTenantId
  ) {
    return { ok: false, reason: "issuer-tenant-mismatch" };
  }
  if (!issuerTrainer) {
    return { ok: false, reason: "issuer-not-found" };
  }

  // Ungebundener Draft: Default einmalig persistieren, dann einfrieren (idempotent).
  if (status === "draft" && !explicitDraftIssuerId && issuerTrainerId) {
    const { error: persistErr } = await sb
      .from("trainerstunden_billing_statements")
      .update({ draft_issuer_trainer_id: issuerTrainerId })
      .eq("id", sid);
    if (persistErr && !isTrainerstundenBillingMissingColumnError(persistErr)) {
      return {
        ok: false,
        reason: "db-error-persist-draft-issuer",
        error: persistErr,
      };
    }
  }

  const issuerSnaps = buildBillingIssuerSnapshotFields(issuerTrainer);

  let paymentSnapshot = null;
  if (issuerTrainerId) {
    const pmR = await sb
      .from("trainerstunden_payment_methods")
      .select("*")
      .eq("trainer_id", issuerTrainerId)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: true });
    if (!pmR.error) {
      paymentSnapshot = snapshotActiveTrainerPaymentMethods(pmR.data || []);
    }
  }

  const peopleIds = Array.from(
    new Set(itemList.map((it) => __asStr(it?.person_id)).filter(Boolean)),
  );
  const peopleById = new Map();
  if (peopleIds.length) {
    const pplR = await sb
      .from("trainerstunden_people")
      .select("id,first_name,last_name")
      .in("id", peopleIds.slice(0, 500));
    if (!pplR.error) {
      for (const p of pplR.data || []) peopleById.set(__asStr(p.id), p);
    }
  }

  const nowIso = new Date().toISOString();
  const trainerVat = resolveTrainerInvoiceVat(issuerTrainer);
  const netTotal = Number(st.total_eur) || 0;
  const vatTotals = computeInvoiceVatTotals(netTotal, trainerVat);

  let layoutSnapshot = buildInvoiceLayoutSnapshot(null);
  let layoutVersionId = null;
  const tenantId = __asStr(issuerTrainer?.tenant_id) || __asStr(st?.tenant_id);
  if (tenantId) {
    const layoutRes = await loadInvoiceLayoutVersions(tenantId);
    if (layoutRes.ok) {
      const version = resolveInvoiceLayoutVersionForDate(
        layoutRes.data || [],
        nowIso,
      );
      if (version) {
        layoutSnapshot = buildInvoiceLayoutSnapshot(version);
        layoutVersionId = version.id;
      }
    }
  }
  const headerUpdate = {
    ...recipientSnaps,
    ...issuerSnaps,
    invoice_payment_snapshot: paymentSnapshot,
    invoice_frozen_at: nowIso,
    issuer_vat_mode_snapshot: trainerVat.mode,
    issuer_vat_rate_snapshot:
      trainerVat.mode === "standard" ? trainerVat.rate : null,
    invoice_net_total_eur: vatTotals.netEur,
    invoice_vat_amount_eur: vatTotals.vatEur,
    invoice_layout_snapshot: layoutSnapshot,
    invoice_layout_version_id: layoutVersionId,
  };

  const { data: updated, error: updErr } = await sb
    .from("trainerstunden_billing_statements")
    .update(headerUpdate)
    .eq("id", sid)
    .select()
    .maybeSingle();
  if (updErr) {
    if (isTrainerstundenBillingMissingColumnError(updErr)) {
      return { ok: true, data: st, snapshotsSkipped: true };
    }
    return { ok: false, reason: "db-error-freeze", error: updErr };
  }

  for (const it of itemList) {
    const pid = __asStr(it?.person_id);
    const person = pid ? peopleById.get(pid) : null;
    const session = resolveBillingStatementItemSession(it, sessions);
    const lineTitle =
      formatBillingStatementItemTitle({
        item: it,
        session: session || sessionsById.get(__asStr(it?.session_id)) || null,
        t: __billingItemDisplayT,
      }) || __asStr(it?.description);
    const personName = __personFirstNameSnapshot(person);
    const patch = {};
    if (lineTitle) patch.line_title_snapshot = lineTitle;
    if (personName) patch.person_name_snapshot = personName;
    if (!Object.keys(patch).length) continue;
    const { error: lineErr } = await sb
      .from("trainerstunden_billing_statement_items")
      .update(patch)
      .eq("id", __asStr(it.id));
    if (lineErr && !isTrainerstundenBillingMissingColumnError(lineErr)) {
      return { ok: false, reason: "db-error-item-freeze", error: lineErr };
    }
  }

  return { ok: true, data: updated || { ...st, ...headerUpdate } };
}
